import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Button, Text, Title } from '@tremor/react'
import { SparklesIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function MethodologyAIGenerationModal({ isOpen, onClose, onSubmit, loading = false }) {
    const { t, i18n } = useTranslation()
    const [activeStep, setActiveStep] = useState(0)
    const [formResponses, setFormResponses] = useState({})
    const [submitError, setSubmitError] = useState('')

    // 8 methodology framework questions
    const methodologyQuestions = [
        {
            id: 1,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q1.Question'),
            options: [
                t('Research.Methodology.Questions.Q1.Option1'),
                t('Research.Methodology.Questions.Q1.Option2'),
                t('Research.Methodology.Questions.Q1.Option3'),
                t('Research.Methodology.Questions.Q1.Option4')
            ]
        },
        {
            id: 2,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q2.Question'),
            options: [
                t('Research.Methodology.Questions.Q2.Option1'),
                t('Research.Methodology.Questions.Q2.Option2'),
                t('Research.Methodology.Questions.Q2.Option3'),
                t('Research.Methodology.Questions.Q2.Option4')
            ]
        },
        {
            id: 3,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q3.Question'),
            options: [
                t('Research.Methodology.Questions.Q3.Option1'),
                t('Research.Methodology.Questions.Q3.Option2'),
                t('Research.Methodology.Questions.Q3.Option3'),
                t('Research.Methodology.Questions.Q3.Option4')
            ]
        },
        {
            id: 4,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q4.Question'),
            options: [
                t('Research.Methodology.Questions.Q4.Option1'),
                t('Research.Methodology.Questions.Q4.Option2'),
                t('Research.Methodology.Questions.Q4.Option3'),
                t('Research.Methodology.Questions.Q4.Option4'),
                t('Research.Methodology.Questions.Q4.Option5')
            ]
        },
        {
            id: 5,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q5.Question'),
            options: [
                t('Research.Methodology.Questions.Q5.Option1'),
                t('Research.Methodology.Questions.Q5.Option2'),
                t('Research.Methodology.Questions.Q5.Option3'),
                t('Research.Methodology.Questions.Q5.Option4')
            ]
        },
        {
            id: 6,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q6.Question'),
            options: [
                t('Research.Methodology.Questions.Q6.Option1'),
                t('Research.Methodology.Questions.Q6.Option2'),
                t('Research.Methodology.Questions.Q6.Option3')
            ]
        },
        {
            id: 7,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q7.Question'),
            options: [
                t('Research.Methodology.Questions.Q7.Option1'),
                t('Research.Methodology.Questions.Q7.Option2'),
                t('Research.Methodology.Questions.Q7.Option3'),
                t('Research.Methodology.Questions.Q7.Option4'),
                t('Research.Methodology.Questions.Q7.Option5')
            ]
        },
        {
            id: 8,
            type: 'multiple_choice',
            question: t('Research.Methodology.Questions.Q8.Question'),
            options: [
                t('Research.Methodology.Questions.Q8.Option1'),
                t('Research.Methodology.Questions.Q8.Option2'),
                t('Research.Methodology.Questions.Q8.Option3'),
                t('Research.Methodology.Questions.Q8.Option4'),
                t('Research.Methodology.Questions.Q8.Option5')
            ]
        }
    ]

    const [additionalContext, setAdditionalContext] = useState('')

    const handleResponseChange = (questionId, value) => {
        setFormResponses(prev => ({
            ...prev,
            [questionId]: value
        }))
    }

    const handleNext = () => {
        if (activeStep < methodologyQuestions.length) {
            setActiveStep(prev => prev + 1)
        }
    }

    const handleBack = () => {
        setActiveStep(prev => prev - 1)
    }

    const handleSubmit = async () => {
        try {
            setSubmitError('')

            // Prepare responses for AI analysis
            const responses = methodologyQuestions.map(question => ({
                question_number: question.id,
                question_text: question.question,
                user_response: formResponses[question.id] || '',
                question_type: question.type
            }))

            // Add additional context if provided
            if (additionalContext.trim()) {
                responses.push({
                    question_number: 9,
                    question_text: t('Research.Methodology.Questions.AdditionalContext'),
                    user_response: sanitizeUserInput(additionalContext),
                    question_type: 'text'
                })
            }

            await onSubmit(responses)
            onClose()
            resetForm()
        } catch (err) {
            console.error('Failed to generate AI methodology suggestions:', err)
            setSubmitError(err.message || t('Research.Methodology.GenerateError'))
        }
    }

    const resetForm = () => {
        setActiveStep(0)
        setFormResponses({})
        setAdditionalContext('')
        setSubmitError('')
    }

    const handleClose = () => {
        onClose()
        resetForm()
    }

    const isLastStep = activeStep === methodologyQuestions.length
    const canProceed = isLastStep || formResponses[methodologyQuestions[activeStep]?.id]
    const allQuestionsAnswered = methodologyQuestions.every(q => formResponses[q.id])

    const renderQuestion = (question) => {
        if (question.type === 'multiple_choice') {
            return (
                <div className="space-y-4">
                    {question.options.map((option, index) => (
                        <button
                            key={index}
                            onClick={() => handleResponseChange(question.id, option)}
                            disabled={loading}
                            className={`w-full p-6 text-left rounded-lg border-2 transition-all ${
                                formResponses[question.id] === option 
                                    ? 'border-iavisus-100 bg-iavisus-50 text-iavisus-900'
                                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            }`}
                        >
                            <div className="font-medium text-lg">
                                {option}
                            </div>
                        </button>
                    ))}
                </div>
            )
        }

        return (
            <div>
                <textarea
                    rows={1}
                    value={formResponses[question.id] || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    placeholder={t('Research.Methodology.Questions.TextPlaceholder')}
                    className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors resize-none overflow-hidden"
                    maxLength={500}
                    disabled={loading}
                    style={{
                        minHeight: 'auto',
                        height: 'auto'
                    }}
                    onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                />
            </div>
        )
    }

    const renderSummaryStep = () => (
        <>
            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                {t('Research.Methodology.Questions.AdditionalContext')}
            </h2>

            <div className="space-y-3">
                <textarea
                    rows={1}
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder={t('Research.Methodology.Questions.AdditionalContextPlaceholder')}
                    className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors resize-none overflow-hidden"
                    maxLength={500}
                    style={{
                        minHeight: 'auto',
                        height: 'auto'
                    }}
                    onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                />
                <Text className="text-gray-500 text-xs">
                    {t('Research.Methodology.Questions.AdditionalContextHelp')}
                </Text>
            </div>
        </>
    )

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t('Research.Methodology.GenerateAI')}
                            </Dialog.Title>
                            <Text className="text-gray-600 mt-1">
                                {t('Research.Methodology.AIGenerationDescription')}
                            </Text>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>


                    {/* Main scrollable content */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-8 max-w-4xl mx-auto h-full flex flex-col">
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="mb-12">
                                    {submitError && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-6">
                                            <Text className="text-red-600 text-sm">{submitError}</Text>
                                        </div>
                                    )}

                                    {isLastStep ? (
                                        renderSummaryStep()
                                    ) : (
                                        <>
                                            <div className="mb-8">
                                                <Text className="text-sm text-gray-500 mb-2">
                                                    {t('Research.Methodology.Questions.StepProgress', {
                                                        current: activeStep + 1,
                                                        total: methodologyQuestions.length
                                                    })}
                                                </Text>
                                                <h2 className="text-2xl md:text-3xl font-light text-gray-900 leading-tight text-left">
                                                    {methodologyQuestions[activeStep].question}
                                                </h2>
                                            </div>
                                            {renderQuestion(methodologyQuestions[activeStep])}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t p-6">
                        <div className="flex justify-between items-center">
                            {activeStep > 0 ? (
                                <Button
                                    variant="secondary"
                                    onClick={handleBack}
                                    disabled={loading}
                                    icon={ChevronLeftIcon}
                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                >
                                    {t('Common.Back')}
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    onClick={handleClose}
                                    disabled={loading}
                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                >
                                    {t('Common.Cancel')}
                                </Button>
                            )}

                            <div className="flex space-x-3">
                                {!isLastStep ? (
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canProceed || loading}
                                        icon={ChevronRightIcon}
                                        iconPosition="right"
                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                    >
                                        {t('Common.Next')}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleSubmit}
                                        icon={SparklesIcon}
                                        disabled={!allQuestionsAnswered || loading}
                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                    >
                                        {loading ? t('Research.Methodology.Generating') : t('Research.Methodology.GenerateMethodology')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default MethodologyAIGenerationModal